import { useState, useEffect, useRef } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Upload, Github, Linkedin, Check, X, CheckCircle2 } from "lucide-react";
import FXBackdrop from "../components/FXBackdrop";
import { useAuth } from "../contexts/AuthContext";

const fade = { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: false, amount: 0.1 }, transition: { duration: 0.6 } };

const inputCls = "h-11 rounded-xl border px-3 bg-white/80 text-slate-900 outline-none focus:ring-2 focus:ring-violet-300 w-full";
const inputStyle = { borderColor: "rgba(226,232,240,0.9)" };
const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Password rules
const pwRules = [
    { label: "At least 8 characters", test: (p) => p.length >= 8 },
    { label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
    { label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
    { label: "One special character", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function EngineerSignUp() {
    const { signupEngineer, isAuthenticated, user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [emailChecking, setEmailChecking] = useState(false);
    const [emailStatus, setEmailStatus] = useState(""); // "available" | "taken" | ""
    const [submitted, setSubmitted] = useState(false);
    const [redirectCountdown, setRedirectCountdown] = useState(3);

    // Used to cancel stale email-check responses (race-condition fix)
    const emailCheckSeq = useRef(0);

    // Redirect already-authenticated users (but not when we're showing the success state)
    useEffect(() => {
        if (authLoading || submitted) return;
        if (isAuthenticated) {
            if (user?.role === "company") navigate("/company/matches", { replace: true });
            else navigate("/engineer/dashboard", { replace: true });
        }
    }, [authLoading, isAuthenticated, user, submitted, navigate]);

    // Countdown to dashboard after successful submission
    useEffect(() => {
        if (!submitted) return;
        if (redirectCountdown <= 0) {
            navigate("/engineer/dashboard", { replace: true });
            return;
        }
        const t = setTimeout(() => setRedirectCountdown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [submitted, redirectCountdown, navigate]);

    // Form state
    const [form, setForm] = useState({
        first_name: "", last_name: "", email: "", password: "", confirm_password: "",
        linkedin_url: "", github_url: "",
        years_experience: "", current_role: "", tech_stack: "", desired_role: "",
        salary_expectation: "", bio: "",
    });
    const [cv, setCv] = useState(null);

    function update(field) {
        return (e) => {
            setForm((f) => ({ ...f, [field]: e.target.value }));
            if (field === "email") {
                // Invalidate any in-flight email check
                emailCheckSeq.current += 1;
                setEmailStatus("");
                setEmailChecking(false);
            }
        };
    }

    // Check email uniqueness — ignores responses from superseded requests
    async function checkEmail() {
        if (!form.email) return;
        const seq = ++emailCheckSeq.current;
        setEmailChecking(true);
        setEmailStatus("");
        try {
            const res = await fetch(`${API}/api/auth/check-email?email=${encodeURIComponent(form.email)}`);
            const data = await res.json();
            if (seq !== emailCheckSeq.current) return; // stale — discard
            setEmailStatus(data.exists ? "taken" : "available");
        } catch {
            if (seq !== emailCheckSeq.current) return;
            setEmailStatus("");
        } finally {
            if (seq === emailCheckSeq.current) setEmailChecking(false);
        }
    }

    // Step 1 — block Next while check is in flight OR status is not confirmed available
    function canProceedStep1() {
        return (
            form.first_name.trim() &&
            form.last_name.trim() &&
            form.email &&
            !emailChecking &&
            emailStatus === "available"
        );
    }

    // Step 2
    function canProceedStep2() {
        return pwRules.every((r) => r.test(form.password)) && form.password === form.confirm_password;
    }

    // Step 4 — required fields gate
    function canSubmitStep4() {
        return (
            form.current_role.trim() &&
            form.tech_stack.trim() &&
            form.desired_role.trim()
        );
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append("first_name", form.first_name);
            fd.append("last_name", form.last_name);
            fd.append("email", form.email);
            fd.append("password", form.password);
            if (form.linkedin_url) fd.append("linkedin_url", form.linkedin_url);
            if (form.github_url) fd.append("github_url", form.github_url);
            if (form.years_experience) fd.append("years_experience", form.years_experience);
            // Required fields — always append
            fd.append("current_role", form.current_role.trim());
            fd.append("tech_stack", form.tech_stack.trim());
            fd.append("desired_role", form.desired_role.trim());
            // Salary — strip non-numeric chars before sending
            if (form.salary_expectation) {
                const numericSalary = form.salary_expectation.replace(/[^0-9]/g, "");
                if (numericSalary) fd.append("salary_expectation", numericSalary);
            }
            if (form.bio) fd.append("bio", form.bio);
            if (cv) fd.append("cv", cv);
            await signupEngineer(fd);
            setSubmitted(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    const stepLabels = ["Your info", "Password", "Profiles", "Experience"];

    if (submitted) {
        return (
            <FXBackdrop>
                <section className="containerX pt-10 md:pt-16 pb-16">
                    <motion.div {...fade} className="max-w-xl mx-auto">
                        <div className="glass p-8 md:p-10 text-center">
                            <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
                                <CheckCircle2 size={36} className="text-emerald-600" />
                            </div>
                            <h1 className="h1 text-3xl md:text-4xl">Records saved successfully</h1>
                            <p className="p mt-3">
                                Welcome to hirrd, <span className="font-medium text-slate-800">{form.first_name}</span>. Your account is ready.
                            </p>
                            <div className="mt-6 text-sm text-slate-500">
                                Redirecting to your dashboard in {redirectCountdown}s...
                            </div>
                            <button
                                type="button"
                                onClick={() => navigate("/engineer/dashboard", { replace: true })}
                                className="btn btnPrimary justify-center mt-6 mx-auto"
                            >
                                Go to dashboard now <ArrowRight size={16} />
                            </button>
                        </div>
                    </motion.div>
                </section>
            </FXBackdrop>
        );
    }

    return (
        <FXBackdrop>
            <section className="containerX pt-10 md:pt-16 pb-16">
                <motion.div {...fade} className="max-w-xl mx-auto">
                    <div className="chip">
                        <span className="h-2 w-2 rounded-full" style={{ background: "rgb(139,92,246)" }} />
                        Engineer Sign Up
                    </div>
                    <h1 className="h1 mt-5 text-3xl md:text-4xl">Join the hirrd network</h1>
                    <p className="p mt-2">Get matched to roles that actually fit.</p>

                    {/* Step indicator */}
                    <div className="flex gap-2 mt-6 mb-2">
                        {[1, 2, 3, 4].map((s) => (
                            <div key={s} className="flex-1 h-1.5 rounded-full transition-all" style={{
                                background: s <= step ? "rgb(139,92,246)" : "rgba(226,232,240,0.9)"
                            }} />
                        ))}
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 mb-6">
                        {stepLabels.map((l, i) => (
                            <span key={i} className={i + 1 <= step ? "text-violet-600 font-medium" : ""}>{l}</span>
                        ))}
                    </div>

                    <form onSubmit={handleSubmit} className="glass p-6 md:p-8">
                        {/* Google OAuth — only on step 1 */}
                        {step === 1 && (
                            <>
                                <div className="grid gap-3 mb-4">
                                    <a href={`${API}/api/auth/google?role=engineer`}
                                        className="h-12 rounded-xl border flex items-center justify-center gap-3 font-medium hover:bg-slate-50 transition"
                                        style={inputStyle}>
                                        <svg width="18" height="18" viewBox="0 0 24 24">
                                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
                                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                        </svg>
                                        Sign up with Google
                                    </a>
                                </div>
                                <div className="flex items-center gap-3 mb-5">
                                    <div className="flex-1 h-px bg-slate-200" />
                                    <span className="text-xs text-slate-400 uppercase tracking-wide">or sign up with email</span>
                                    <div className="flex-1 h-px bg-slate-200" />
                                </div>
                            </>
                        )}

                        {error && (
                            <div className="mb-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
                                {error}
                            </div>
                        )}

                        {/* Step 1: Name + Email */}
                        {step === 1 && (
                            <div className="grid gap-3">
                                <div className="font-semibold text-lg mb-1">Your information</div>
                                <div className="grid grid-cols-2 gap-3">
                                    <input className={inputCls} style={inputStyle} placeholder="First name *" value={form.first_name} onChange={update("first_name")} required />
                                    <input className={inputCls} style={inputStyle} placeholder="Last name *" value={form.last_name} onChange={update("last_name")} required />
                                </div>
                                <div className="relative">
                                    <input
                                        className={inputCls}
                                        style={inputStyle}
                                        placeholder="Email *"
                                        type="email"
                                        value={form.email}
                                        onChange={update("email")}
                                        onBlur={checkEmail}
                                        required
                                    />
                                    {emailChecking && (
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">Checking...</span>
                                    )}
                                    {!emailChecking && emailStatus === "available" && (
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500"><Check size={16} /></span>
                                    )}
                                    {!emailChecking && emailStatus === "taken" && (
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500"><X size={16} /></span>
                                    )}
                                </div>
                                {emailStatus === "taken" && (
                                    <div className="text-sm text-red-600 -mt-1">User already signed up as {form.email}. <NavLink to="/login" className="text-violet-600 underline">Log in instead?</NavLink></div>
                                )}
                                <button type="button" onClick={() => setStep(2)} disabled={!canProceedStep1()}
                                    className="btn btnPrimary justify-center mt-2 disabled:opacity-50 disabled:cursor-not-allowed">
                                    {emailChecking ? "Checking email..." : "Next"} <ArrowRight size={16} />
                                </button>
                            </div>
                        )}

                        {/* Step 2: Password */}
                        {step === 2 && (
                            <div className="grid gap-3">
                                <div className="font-semibold text-lg mb-1">Create password</div>
                                <input className={inputCls} style={inputStyle} placeholder="Password *" type="password" value={form.password} onChange={update("password")} required />
                                <input className={inputCls} style={inputStyle} placeholder="Confirm password *" type="password" value={form.confirm_password} onChange={update("confirm_password")} required />

                                {/* Password rules */}
                                <div className="rounded-xl border p-3 grid gap-1.5" style={inputStyle}>
                                    {pwRules.map((rule, i) => {
                                        const pass = rule.test(form.password);
                                        return (
                                            <div key={i} className={`flex items-center gap-2 text-xs ${pass ? "text-green-600" : "text-slate-400"}`}>
                                                {pass ? <Check size={12} /> : <X size={12} />}
                                                {rule.label}
                                            </div>
                                        );
                                    })}
                                    <div className={`flex items-center gap-2 text-xs ${form.password && form.confirm_password && form.password === form.confirm_password ? "text-green-600" : "text-slate-400"}`}>
                                        {form.password && form.confirm_password && form.password === form.confirm_password ? <Check size={12} /> : <X size={12} />}
                                        Passwords match
                                    </div>
                                </div>

                                <div className="flex gap-2 mt-2">
                                    <button type="button" onClick={() => setStep(1)} className="btn btnGhost flex-1">Back</button>
                                    <button type="button" onClick={() => setStep(3)} disabled={!canProceedStep2()}
                                        className="btn btnPrimary flex-1 disabled:opacity-50">Next <ArrowRight size={16} /></button>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Links + CV */}
                        {step === 3 && (
                            <div className="grid gap-3">
                                <div className="font-semibold text-lg mb-1">Your profiles</div>
                                <div className="relative">
                                    <Linkedin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input className={inputCls + " pl-9"} style={inputStyle} placeholder="LinkedIn URL" value={form.linkedin_url} onChange={update("linkedin_url")} />
                                </div>
                                <div className="relative">
                                    <Github size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input className={inputCls + " pl-9"} style={inputStyle} placeholder="GitHub URL" value={form.github_url} onChange={update("github_url")} />
                                </div>
                                <div>
                                    <label className="flex items-center gap-3 cursor-pointer rounded-xl border px-4 py-3 hover:bg-white/50 transition"
                                        style={inputStyle}>
                                        <Upload size={18} className="text-violet-500" />
                                        <span className="text-sm">{cv ? cv.name : "Upload your CV (PDF, DOC)"}</span>
                                        <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setCv(e.target.files[0])} />
                                    </label>
                                </div>
                                <div className="flex gap-2 mt-2">
                                    <button type="button" onClick={() => setStep(2)} className="btn btnGhost flex-1">Back</button>
                                    <button type="button" onClick={() => setStep(4)} className="btn btnPrimary flex-1">Next <ArrowRight size={16} /></button>
                                </div>
                            </div>
                        )}

                        {/* Step 4: Experience */}
                        {step === 4 && (
                            <div className="grid gap-3">
                                <div className="font-semibold text-lg mb-1">Experience & preferences</div>
                                <input className={inputCls} style={inputStyle} placeholder="Years of experience" type="number" min="0" value={form.years_experience} onChange={update("years_experience")} />
                                <input className={inputCls} style={inputStyle} placeholder="Current role (e.g. Senior Engineer at ACME) *" value={form.current_role} onChange={update("current_role")} required />
                                <input className={inputCls} style={inputStyle} placeholder="Programming languages (comma-separated, e.g. Python, TypeScript) *" value={form.tech_stack} onChange={update("tech_stack")} required />
                                <input className={inputCls} style={inputStyle} placeholder="Preferred role (e.g. Full-Stack Engineer) *" value={form.desired_role} onChange={update("desired_role")} required />
                                {/* Salary with hard-coded $ prefix */}
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium select-none">$</span>
                                    <input
                                        className={inputCls + " pl-7"}
                                        style={inputStyle}
                                        placeholder="Required salary (e.g. 120000)"
                                        type="number"
                                        min="0"
                                        value={form.salary_expectation}
                                        onChange={update("salary_expectation")}
                                    />
                                </div>
                                <textarea className="min-h-20 rounded-xl border px-3 py-2 bg-white/80 text-slate-900 outline-none focus:ring-2 focus:ring-violet-300 w-full"
                                    style={inputStyle} placeholder="Short bio (optional)" value={form.bio} onChange={update("bio")} />
                                <div className="flex gap-2 mt-2">
                                    <button type="button" onClick={() => setStep(3)} className="btn btnGhost flex-1">Back</button>
                                    <button type="submit" disabled={loading || !canSubmitStep4()} className="btn btnPrimary flex-1 disabled:opacity-50">
                                        {loading ? "Creating account..." : "Create account"} {!loading && <ArrowRight size={16} />}
                                    </button>
                                </div>
                                <p className="text-xs text-slate-400 -mt-1">Fields marked * are required.</p>
                            </div>
                        )}

                        <div className="text-center text-sm text-slate-500 mt-4">
                            Already have an account? <NavLink to="/login" className="text-violet-600 font-medium hover:underline">Log in</NavLink>
                        </div>
                    </form>
                </motion.div>
            </section>
        </FXBackdrop>
    );
}
